<template>
  <v-row justify="center">
    <v-dialog v-model="dialog" scrollable max-width="600px">
      <v-card>
        <v-card-title class="grey--text text-h5">
          Visitor
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="mb-0 pb-0">
          <v-form class="pt-7 pb-0" ref="form" v-model="valid" lazy-validation>
            <v-text-field dense v-model="visitor.guest_name" label="Full Name" :error-messages="apiErrors.guest_name"
              outlined :rules="rules.required"></v-text-field>

            <!--  Phone Number -->
            <div class="pb-2">
              <template>
                <vue-phone-number-input v-model="phoneNumber" @update="updatePhoneNumber"
                  :default-country-code="national_number" size="md"></vue-phone-number-input>
                <span class="text-caption pl-4 red--text">{{ apiErrors.phone ? apiErrors.phone[0] : '' }}</span>
                <span class="text-caption red--text" v-if="phoneValidationError">{{ phoneValidationError }}</span>
              </template>
            </div>

            <v-row>
              <!-- Company Name -->
              <v-col>
                <v-text-field dense v-model="organization.legal_name" label="Company Name" readonly
                  :error-messages="apiErrors.companyName" outlined></v-text-field>
              </v-col>

              <!--  Location -->
              <v-col>
                <v-select 
                  dense 
                  v-model="visitor.facility_id" 
                  :items="organizationFacilities" 
                  label="Location"
                  :error-messages="apiErrors.facility_id" 
                  item-text="facility" 
                  item-value="id" 
                  outlined
                  :rules="rules.select"
                  :pre-select-first="true"
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <!-- Date -->
              <v-col>
                <v-menu :close-on-content-click="closeOnContentClick" max-width="290px" min-width="290px">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-text-field dense v-model="dateToday" label="Date" v-bind="attrs" v-on="{ ...menu }"
                      :error-messages="apiErrors.event_date" outlined>
                    </v-text-field>
                  </template>
                  <v-date-picker v-model="dateToday" color="primary" header-color="primary"></v-date-picker>
                </v-menu>
              </v-col>

              <!-- From -->
              <v-col>
                <v-menu :close-on-content-click="closeOnContentClick" max-width="290px" min-width="290px">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-text-field dense v-model="startTime" label="From" v-bind="attrs" v-on="{ ...menu }"
                      :error-messages="apiErrors.from_time" outlined>
                    </v-text-field>
                  </template>
                  <v-time-picker v-model="startTime" scrollable format="24hr"></v-time-picker>
                </v-menu>
              </v-col>

              <!-- To -->
              <v-col>
                <v-menu :close-on-content-click="closeOnContentClick" max-width="290px" min-width="290px">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-text-field dense v-model="endTime" label="To" v-bind="attrs" v-on="{ ...menu }"
                      :error-messages="apiErrors.end_time" outlined>
                    </v-text-field>
                  </template>
                  <v-time-picker v-model="endTime" scrollable format="24hr"></v-time-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-select dense v-model="visitor.category" :items="visitCategories" label="Category"
              :error-messages="apiErrors.category" outlined></v-select>

            <v-autocomplete 
              dense 
              v-model="visitor.host_id" 
              :search-input.sync="search"
              :items="organizationUsers" 
              item-value="id"
              item-text="attributes.name" 
              label="Host" 
              :error-messages="apiErrors.host_id" 
              outlined
              autocomplete="off"
              :loading="loading"
              :readonly="host?.id ? true : false"
            ></v-autocomplete>

            <v-text-field dense v-model="visitor.title" label="Title" :error-messages="apiErrors.title" outlined>
            </v-text-field>

            <!-- <v-text-field dense v-model="visitor.reason" label="Reason" :error-messages="apiErrors.reason" outlined>
            </v-text-field> -->
            <!-- </v-container> -->
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn color="red darken-1" text @click="closeModal" class="text-capitalize mx-1">
            Cancel
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn color="green darken-1 white--text" @click="saveVisit" class="text-capitalize mx-1"
            :loading="submitting">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import Visitor from '../models/visitor';
import EventsService from '../services/events.service';
import OrganizationService from '../services/organization.service';

export default {
  props: {
    value: {
      type: Boolean,
      required: true
    },
    organizationFacilities: {
      type: Array,
      required: true
    },
    organization: {
      type: Object,
      required: true
    },
    selectedDate: {
      type: String,
      required: false
    },
    host: {
      type: Object,
      required: false
    },
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },
  created() {
    this.visitor = new Visitor();
    this.calculateTime();
  },
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 10,
        filter: null
      },
      search: null,
      organizationUsers: [],
      visitor: new Visitor(),
      national_number: "KE",
      phoneNumber: null,
      phoneValidationError: null,
      dateToday: new Date().toISOString().substr(0, 10),
      startTime: null,
      endTime: null,
      visitCategories: ["Official", "Personal"],
      apiErrors: {},
      valid: false,
      submitting: false,
      loading: false,
      closeOnContentClick: false,
      rules: {
        select: [(v) => !!v || "An item should be selected"],
        select2: [(v) => v.length > 0 || "Atleast one item should be selected"],
        required: [
          (v) => !!v || "Field is required",
          (v) => (v && v.length >= 3) || "Min 3 characters",
        ],
        file: [
          (v) => !!v || "File is required",
          (v) => (v && v.length > 0) || "File is required",
        ],
      },
    }
  },
  watch: {
    dialog: {
      handler(value) {
        if (value) {
          this.getUsers();
          this.visitor.category = "Official";
          this.calculateTime();
        }
      },
      deep: true
    },
    search: {
      handler(value) {
        this.getUsers(value);
      },
      deep: true
    },
    selectedDate: {
      handler(value) {
        this.dateToday = value;
      },
      deep: true
    },
  },
  methods: {
    calculateTime() {
      let time = new Date(Date.now());
      let hour = time.getHours();
      let minutes = time.getMinutes();

      if (minutes > 30) {
        hour += 2;
      } else {
        hour += 1;
      }

      this.startTime = hour + ":00";
      this.endTime = (hour + 1) + ":00";
    },
    closeModal() {
      this.phoneNumber = null;
      this.visitor = new Visitor();
      this.dialog = false;
    },
    saveVisit() {
      this.valid = this.$refs.form.validate();
      if (this.valid) {
        this.validatePhoneNumber();
        
        this.visitor.from_time = this.startTime;
        this.visitor.end_time = this.endTime;
        this.visitor.event_date = this.dateToday;

        this.submitting = true;

        EventsService.store(this.visitor).then(
          (response) => {
            if (response.status === 200) {
              this.$store.dispatch("alert/success", "Visitor invited");
              this.loading = false;
              this.submitting = false;
              this.$emit('get_upcoming_visits');
            } else {
              this.submitting = false;
              this.$store.dispatch("alert/error", (this.user.name ?? "Visitor") + " not invited" + response.message);
            }

            this.closeModal();
          },
          (error) => {
            this.submitting = false;

            if (error.response.status === 422) {
              this.apiErrors = error.response.data.errors
              console.log(error.response.data.errors)
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
            }

            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      } else {
        this.$store.dispatch("alert/error", 'Form is Invalid!');
      }
    },
    updatePhoneNumber(data) {
      this.visitor.guest_phone = data.e164
    },
    validatePhoneNumber() {
      if (this.phoneNumber == null) {
        this.phoneErrorColor = "red";
        this.phoneValidationError = "The phone number is required";
      }
    },
    getUsers(search = null) {
      this.loading = true;
      if (!this.host?.id) {
        this.options.filter = search;
      } else {
        this.options.filter = this.host?.name;
      }

      OrganizationService.organization_users(this.organization.id, { options: this.options }).then(
        success => {
          this.organizationUsers = success?.data?.data?.data ?? this.organizationUsers;
          this.loading = false;

          if (this.host?.id) {
            let index = -1;
            index = this.organizationUsers.findIndex(user => user.id == this.host?.id);
            if (index > -1) {
              this.visitor.host_id = this.organizationUsers[index].id;
            }
          }
        },
        error => {
          console.log(error)
          this.organizationUsers = [];
        }
      )
    },
  },
}
</script>